import { SelectFamilyGroupMembers } from "@components/auto-completes/family-members";
import { Controller, useFormContext } from "react-hook-form";
import {
  AnalysisSetupDTO,
  AnalysisSetupStepRenderedFieldProps,
} from "@models/platform-analysis/entities/steps/setup";
import { useEffect, useState } from "react";
import { SelectedFamilyMemberDTO } from "@models/reviews/entities/steps/setup";
import { useIsNew } from "@hooks/useIsNew";
import { useParams } from "react-router";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";

export const AnalysisSetupStepFamilyMembersField = ({
  disabled,
}: AnalysisSetupStepRenderedFieldProps) => {
  const isNew = useIsNew();
  const { analysisId } = useParams<{ analysisId: string }>();
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AnalysisSetupDTO>();
  const { getFamilyMembers } = usePlatformAnalysis();
  const watchedFamilyGroupId = watch("familyGroupId");
  const watchedSelectedFamilyMemberIds = watch("selectedFamilyMemberIds") || [];

  const [hasRefreshData, toggleRefreshData] = useState(false);
  const [familyGroupMemberData, setFamilyGroupMemberData] =
    useState<SelectedFamilyMemberDTO[]>();

  const handleOnFamilyMemberIdsChange = (ids: string[] | string) => {
    const selectedIds = typeof ids === "string" ? [ids] : ids;
    const selectedMembers: SelectedFamilyMemberDTO[] =
      familyGroupMemberData?.map((member) => ({
        ...member,
        isSelected: selectedIds.includes(member.order?.toString() ?? ""),
      })) ?? [];
    setValue("selectedFamilyMemberIds", selectedIds);
    setValue("selectedFamilyMembers", selectedMembers);
  };

  useEffect(() => {
    if (!watchedFamilyGroupId) {
      setValue("selectedFamilyMemberIds", []);
      setValue("selectedFamilyMembers", []);
      setFamilyGroupMemberData([]);
      return;
    }

    getFamilyMembers(
      isNew || hasRefreshData ? "new" : analysisId ?? "",
      watchedFamilyGroupId,
    ).then((response) => {
      setFamilyGroupMemberData(response || []);
      const selectedIds = response
        ?.filter((member) => member.isSelected)
        .map((member) => member.order?.toString() ?? "") as string[];
      setValue("selectedFamilyMemberIds", selectedIds);
      setValue("selectedFamilyMembers", response);
      toggleRefreshData(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFamilyGroupId]);

  return (
    <Controller
      name={"selectedFamilyMemberIds"}
      control={control}
      render={({ field: { onChange, ...other } }) => (
        <SelectFamilyGroupMembers
          {...other}
          onChange={(selectedFamilyMemberIds) => {
            onChange(selectedFamilyMemberIds);
            handleOnFamilyMemberIdsChange(selectedFamilyMemberIds ?? []);
          }}
          multiple
          value={watchedSelectedFamilyMemberIds}
          disabled={!watchedFamilyGroupId || disabled}
          familyGroupMemberData={familyGroupMemberData ?? []}
          maxLength={familyGroupMemberData?.length}
          textFieldProps={{
            label:
              "Select up to eight family members/entities to include in your review",
            placeholder: "Search for members",
            error: !!errors?.selectedFamilyMemberIds,
            helperText: errors?.selectedFamilyMemberIds?.message,
            required: true,
          }}
        />
      )}
    />
  );
};
